import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  useUpdateUserMutation,
  useCreateUserMutation,
} from "../services/userService";
import toast from "react-hot-toast";
import {
  Button,
  TextField,
  CircularProgress,
  Box,
  Modal,
  Paper,
  Typography,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { X } from "lucide-react";
import { useGetHospitalsQuery } from "../services/hospitalService";

interface UserFormData {
  name: string;
  email: string;
  phone: string;
  role: string;
  hospital: string;
  isActive: boolean;
}

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user?: any;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [loading, setLoading] = useState(false);
  const isEdit = !!user;

  // Add these inside your component, before the return statement
  const { data: hospitals = [] } = useGetHospitalsQuery({});

  const roles = [
    { value: "admin", label: "Admin" },
    { value: "hospital_admin", label: "Hospital Admin" },
    { value: "user", label: "User" },
  ];

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      role: "user",
      hospital: "",
      isActive: true,
    },
  });

  // Reset form when user data changes
  useEffect(() => {
    if (user) {
      reset({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        role: user.role || "hospital_admin",
        hospital: user.hospital?.name || "",
        isActive: user.isActive !== false, // Default to true if undefined
      });
    } else {
      reset({
        name: "",
        email: "",
        phone: "",
        role: "hospital_admin",
        hospital: "",
        isActive: true,
      });
    }
  }, [user, reset]);

  const [updateUser] = useUpdateUserMutation();
  const [createUser] = useCreateUserMutation();

  const onSubmit = async (data: UserFormData) => {
    setLoading(true);
    try {
      if (isEdit && user?._id) {
        await updateUser({ id: user._id, ...data }).unwrap();
        toast.success("User updated successfully");
      } else {
        // Add password field for new users
        await createUser({
          ...data,
          password: generateTemporaryPassword(),
        }).unwrap();
        toast.success("User created successfully");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error("Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate a temporary password
  const generateTemporaryPassword = (): string => {
    const length = 10;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="user-form-modal"
      aria-describedby="user-form-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiBackdrop-root": {
          background: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: 500,
          maxHeight: "90vh",
          overflowY: "auto",
          p: 3,
          position: "relative",
        }}
        elevation={3}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" component="h2">
            {isEdit ? "Edit User" : "Add New User"}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Full Name"
              variant="outlined"
              {...register("name", { required: "Name is required" })}
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              {...register("email", { required: "Email is required" })}
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Phone"
              variant="outlined"
              {...register("phone", { required: "Phone is required" })}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              sx={{ mb: 2 }}
            />

            <Controller
              name="role"
              control={control}
              defaultValue=""
              rules={{ required: "Role is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={roles}
                  getOptionLabel={(option) => option.label}
                  value={roles.find((r) => r.value === field.value) || null}
                  onChange={(_, data) => field.onChange(data?.value || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Role"
                      variant="outlined"
                      error={!!errors.role}
                      helperText={errors.role?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              )}
            />

            <Controller
              name="hospital"
              control={control}
              rules={{ required: "Hospital is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={hospitals.data}
                  getOptionLabel={(option) => option.name}
                  value={hospitals.data.find((h: any) => h._id === field.value) || null}
                  onChange={(_, data) => field.onChange(data?._id || "")}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value?._id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Hospital"
                      variant="outlined"
                      error={!!errors.hospital}
                      helperText={errors.hospital?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                  }
                />
              )}
            />

            <Box
              sx={{
                mt: 3,
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button variant="outlined" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default UserFormModal;
